import React, { useCallback, useEffect, useState } from 'react';
import { addDoc, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from '../firebase/firebaseConfig';
import ShowroomTopImageUpload from './ShowroomTopImageUpload';
import ShowroomTopTextEditor from './ShowroomTopTextEditor';
import ShowroomRoomEditor from './ShowroomRoomEditor';
import {
  SHOWROOM_ROOMS_COLLECTION,
  TOP_TAB_ID,
} from '../components/showroom/showroomSettings';

const roomsCollection = () => collection(firestore, SHOWROOM_ROOMS_COLLECTION);

const ShowroomManagement = () => {
  const [activeTab, setActiveTab] = useState(TOP_TAB_ID);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const ensureDefaultRoom = useCallback(async (existingRooms) => {
    if (existingRooms.length > 0) return existingRooms;

    const docRef = await addDoc(roomsCollection(), {
      name: 'ショールーム1',
      order: 1,
      published: false,
      createdAt: new Date(),
    });

    return [{ id: docRef.id, name: 'ショールーム1', order: 1, published: false }];
  }, []);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const roomsQuery = query(roomsCollection(), orderBy('order', 'asc'));
        const snapshot = await getDocs(roomsQuery);
        const roomData = snapshot.docs.map((roomDoc) => ({
          id: roomDoc.id,
          ...roomDoc.data(),
        }));
        const ensuredRooms = await ensureDefaultRoom(roomData);
        setRooms(ensuredRooms);
      } catch (error) {
        console.error('ショールーム一覧の取得に失敗:', error);
        setErrorMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRooms();
  }, [ensureDefaultRoom]);

  const addRoom = async () => {
    if (isAdding) return;

    try {
      setIsAdding(true);
      setErrorMessage('');
      const nextOrder = rooms.reduce((max, room) => Math.max(max, room.order || 0), 0) + 1;
      const name = `ショールーム${nextOrder}`;
      const docRef = await addDoc(roomsCollection(), {
        name,
        order: nextOrder,
        published: false,
        createdAt: new Date(),
      });
      const newRoom = { id: docRef.id, name, order: nextOrder, published: false };
      setRooms((prev) => [...prev, newRoom]);
      setActiveTab(docRef.id);
    } catch (error) {
      console.error('ショールームの追加に失敗:', error);
      setErrorMessage(error.message);
      alert(`エラー: ${error.message}`);
    } finally {
      setIsAdding(false);
    }
  };

  const styles = {
    wrapper: {
      width: '100%',
    },
    tabBar: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      width: '100%',
      padding: '12px 20px 0',
      boxSizing: 'border-box',
      backgroundColor: '#fafafa',
      borderBottom: '1px solid #ddd',
    },
    tab: {
      padding: '8px 16px',
      cursor: 'pointer',
      backgroundColor: 'white',
      marginRight: '4px',
      marginBottom: '8px',
      border: '1px solid #ddd',
    },
    activeTab: {
      backgroundColor: '#ddd',
      fontWeight: 'bold',
    },
    unpublishedBadge: {
      marginLeft: '8px',
      padding: '2px 6px',
      borderRadius: '4px',
      backgroundColor: '#808080',
      color: 'white',
      fontSize: '11px',
      fontWeight: 'normal',
    },
    addTab: {
      padding: '8px 16px',
      cursor: 'pointer',
      backgroundColor: 'white',
      marginRight: '4px',
      marginBottom: '8px',
      border: '1px dashed #999',
      color: '#110b41ff',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
    },
    status: {
      color: '#666',
      fontSize: '14px',
      padding: '40px 20px',
    },
    error: {
      color: 'red',
      fontSize: '14px',
      margin: '12px 20px 0',
      textAlign: 'left',
    },
  };

  const activeRoom = rooms.find((room) => room.id === activeTab);

  return (
    <div style={styles.wrapper}>
      <div style={styles.tabBar}>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === TOP_TAB_ID ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab(TOP_TAB_ID)}
        >
          トップ
        </div>
        {rooms.map((room) => (
          <div
            key={room.id}
            style={{
              ...styles.tab,
              ...(activeTab === room.id ? styles.activeTab : {}),
            }}
            onClick={() => setActiveTab(room.id)}
          >
            {room.name}
            {room.published === false && (
              <span style={styles.unpublishedBadge}>非公開</span>
            )}
          </div>
        ))}
        <div
          style={{
            ...styles.addTab,
            cursor: isAdding || isLoading ? 'not-allowed' : 'pointer',
            opacity: isAdding || isLoading ? 0.6 : 1,
          }}
          onClick={isLoading ? undefined : addRoom}
        >
          {isAdding ? '追加中...' : '＋ 追加'}
        </div>
      </div>

      {errorMessage && <p style={styles.error}>エラー: {errorMessage}</p>}

      {activeTab === TOP_TAB_ID && (
        <div style={styles.container}>
          <ShowroomTopImageUpload />
          <ShowroomTopTextEditor />
        </div>
      )}

      {activeTab !== TOP_TAB_ID && isLoading && (
        <p style={styles.status}>読み込み中...</p>
      )}

      {activeRoom && (
        <ShowroomRoomEditor
          key={activeRoom.id}
          room={activeRoom}
          onSaved={(updatedRoom) => {
            setRooms((prev) =>
              prev.map((room) => (room.id === updatedRoom.id ? { ...room, ...updatedRoom } : room))
            );
          }}
          onDeleted={(roomId) => {
            const deletedIndex = rooms.findIndex((item) => item.id === roomId);
            const remaining = rooms.filter((item) => item.id !== roomId);
            setRooms(remaining);
            if (activeTab === roomId) {
              const nextRoom = remaining[deletedIndex] || remaining[deletedIndex - 1];
              setActiveTab(nextRoom ? nextRoom.id : TOP_TAB_ID);
            }
          }}
        />
      )}
    </div>
  );
};

export default ShowroomManagement;
